
import { Options, Vue } from "vue-class-component";
import Dialog from "@/app/ui/components/dialog/index.vue";
import DataWrapper from "@/app/ui/components/data-wrapper/index.vue";
import CustomDropdown from "@/app/ui/components/custom-dropdown/index.vue";
import Select from "@/app/ui/components/select/index.vue";
import LpInput from "@/app/ui/components/lp-input/index.vue";
import Icons from "@/app/ui/components/icons/index.vue";
import { CargoV2Controller } from "@/app/ui/controllers/CargoV2Controller";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { OptionsClass } from "@/domain/entities/MainApp";
import { RtcData } from "@/domain/entities/CargoV2";
import parsingErrorResponse from "@/app/infrastructures/misc/common-library/ParsingErrorResponse";
import { formatDate } from "@/app/infrastructures/misc/Utils";
import { CheckEligible, useCargoV2 } from "../composables";
import { buildShcOptions, isPlaneNogValid } from "../utils";

const { onCheckEligible } = useCargoV2();

@Options({
  components: {
    Dialog,
    DataWrapper,
    CustomDropdown,
    Select,
    LpInput,
    Icons
  },
  emits: ["update:modelValue", "submit"],
  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    rtcData: {
      type: Object,
      required: true
    },
    submitTitle: {
      type: String,
      default: ""
    }
  }
})
class ModalPlaneAutoV3 extends Vue {
  modelValue!: boolean;
  rtcData!: RtcData;
  submitTitle!: string;

  isOpenSelectCargoProduct = false;

  get show() {
    return this.modelValue;
  }

  set show(val: boolean) {
    this.$emit("update:modelValue", val);
    if (val) this.initModalData();
  }

  onReset() {
    this.show = false;
  }

  get form() {
    return CargoV2Controller.form;
  }

  get commodityOptions(): OptionsClass[] {
    return CargoV2Controller.commodityOptions;
  }

  get commodityItem() {
    return CargoV2Controller.form.cargoCommodity;
  }

  get disableCommodityGroup(): boolean {
    return this.commodityOptions.length === 1;
  }

  get listProductCargo() {
    const defaultChoices: any = [
      {
        name: this.$t("cargoV2.components.common.selectCargoProductType"),
        value: "",
        status: ""
      }
    ];
    const cargos = CargoV2Controller.cargoProductList.map((e: any) => ({
      name: e.cargoProductTypeCode,
      value: e.cargoProductTypeCode,
      status: e.cargoProductTypeStatus
    }));

    return defaultChoices.concat(cargos);
  }

  get isNogError() {
    return !isPlaneNogValid(this.rtcData.cargoType, this.form.nog);
  }

  get isSubmitDisabled() {
    const hasEmptyRequiredField =
      !this.commodityItem?.name || !this.form.productCargo;

    return hasEmptyRequiredField || this.isNogError;
  }

  onSelectShc(value: any) {
    this.form.shc = value.value;
  }

  get listShc() {
    return buildShcOptions(
      CargoV2Controller.commodityOptions,
      this.commodityItem
    );
  }

  get shcItem() {
    return new OptionsClass({
      name: this.form.shc || "-",
      value: this.form.shc
    });
  }

  onSelectCommodity(val: any) {
    if (val.value === this.commodityItem.value) {
      CargoV2Controller.form.cargoCommodity = new OptionsClass({
        value: "",
        name: ""
      });
      CargoV2Controller.updateCargoCommodity(new OptionsClass());
      return;
    }

    CargoV2Controller.form.cargoCommodity = val;
    CargoV2Controller.updateCargoCommodity(
      new OptionsClass({
        name: val.name,
        value: val.value,
        shcCode: val.shcCode,
        shcName: val.shcName
      })
    );
  }

  onChangeNog(value: string) {
    CargoV2Controller.updateNog(value);
  }

  onOpenSelectCargoProduct() {
    this.isOpenSelectCargoProduct = true;
  }

  onCloseSelectCargoProduct() {
    this.isOpenSelectCargoProduct = false;
  }

  onSelectCargoProduct(_: string, value: string) {
    this.onCloseSelectCargoProduct();
    CargoV2Controller.updateSelectedCargoProduct(value);
  }

  get formatDateVal() {
    if (this.rtcData?.nextCargoFlightTime) {
      return formatDate(
        this.rtcData.nextCargoFlightTime,
        "DD MMM YYYY, HH.mm",
        false,
        false
      );
    }

    const firstFlight = this.rtcData?.flightsData?.[0];
    if (!firstFlight?.departureHour) return "-";

    return this.$moment(firstFlight.departureHour, "HH:mm:ss")
      .add(firstFlight.flightDayTransit || 0, "day")
      .format("DD MMM YYYY, HH.mm");
  }

  async initModalData() {
    try {
      await CargoV2Controller.getCargoCommodityList();
      await CargoV2Controller.getCargoProductList("active");

      if (!CargoV2Controller.cargoProductList.length) return;

      const productFound = CargoV2Controller.cargoProductList.find(
        (product: any) =>
          product.cargoProductTypeCode === this.form.productCargo
      );
      if (!productFound) {
        CargoV2Controller.updateSelectedCargoProduct(
          CargoV2Controller.cargoProductList[0].cargoProductTypeCode
        );
      }
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(error, this.$t("cargoV2.components.common.errorGetData"), () =>
          this.initModalData()
        )
      );
    }
  }

  onSubmit() {
    onCheckEligible(
      new CheckEligible({
        success: () => {
          this.$emit("submit");
          this.show = false;
          MainAppController.closeMessageModal();
        },
        close: () => {
          this.show = false;

          this.form.actualTotalCargoVolumeweight = this.form.totalAccumulationEstimationVolumeWeightDataTable;
          this.form.cargoActualPiece = this.form.totalAccumulationActualPieceDataTable;
          this.form.actualTotalCargoGrossweigth = this.form.totalAccumulationEstimationGrossWeightDataTable;
          CargoV2Controller.setForm(this.form);
          this.$router.push(`/cargo-v2/create/rtc/${this.rtcData.id}`);
        }
      })
    );
  }
}

export default ModalPlaneAutoV3;
